<!-- @format -->
<script setup>
import SmartBox from '../common/SmartBox.vue';
import DestinationSearch from './components/DestinationSearch.vue';
import Occupancy from './components/Occupancy.vue';
import SearchInput from './components/SearchInput.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import { storeToRefs } from 'pinia'
import { usePackageBookerStore } from '../stores/packageBooker'
import { ref, getCurrentInstance, onMounted, computed } from 'vue'

const { proxy } = getCurrentInstance()
const __ = proxy.__
const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const defaultDate = fn.getDate();
const guestsBox = ref(null)
const calendarBox = ref(null)
const isMobile = ref(false);

const store = usePackageBookerStore()
const {
  form,
  fieldErrors,
  book,
  recentSearches,
  popularDestinations,
  searchResults,
  algoliaUrl,
  OpenSearch,
  CloseSearch,
  lastQuery,
  settingPaxes,
  localeDatePicker
} = storeToRefs(store)

const calendarDates = computed({
  get() {
    return {
      start: book.value.checkIn ? new Date(book.value.checkIn) : null,
      end: book.value.checkOut ? new Date(book.value.checkOut) : null
    };
  },
  set(val) {
    book.value.checkIn = val.start ? new Date(val.start) : null;
    book.value.checkOut = val.end ? new Date(val.end) : null;
    updateFormDates(book.value.checkIn, book.value.checkOut);
  }
});

function onSelectDates(val) {
  updateFormDates(val.start, val.end);
  book.value.checkIn = val.start;
  book.value.checkOut = val.end;
  if (calendarBox.value) {
    calendarBox.value.close();
  }
}

let origin = null;
function isValidDate(dateFrom, dateTo) {
  let valid = true;
  dateFrom.setHours(0, 0, 0, 0);
  if (+dateFrom > +dateTo) {
    valid = false;
  }
  return valid;
}

function initDatePicker() {
  let valid = isValidDate(book.value.checkIn, book.value.checkOut);
  if (!valid) {
    book.value.checkOut = store.getInitialDate(book.value.checkIn, config.bookerDefaultCheckOutDay);
  }
  updateFormDates(book.value.checkIn, book.value.checkOut);
}

function updateFormDates(start, end) {
  if (!start || !end) return;
  const format = localeDatePicker.value.format;
  form.value.dates = `${moment(start).format(format).replace(/\./g, '')} - ${moment(end).format(format).replace(/\./g, '')}`;
}

function onOpenSearch(element) {
  setTimeout(() => {
    store.fetchPopularDestinations(element, origin);
  }, 200);
}

async function onSearch(value, mobile, list) {
  if (mobile && list) {
    form.value[list] = value;
  }
  await store.fetchSearchResults(value, mobile, list);
}

function onSelectDestination(item, type, suffix = '') {
  const code = store.selectDestination(item, type, suffix);
  store.setFieldError(type, "");
  if (type === 'from') {
    origin = code;
  }
}

function onChangePaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
}

function onApplyPaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
  isValidFormApply();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}

function onCancelPaxes() {
  store.updateInputPaxes();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}

function onCloseGuests() {
  store.updateInputPaxes();
}
function isValidFormApply() {
  if (store.validateForm(true)) {
    submitSearch();
  }
}
function submitSearch() {
  store.submitSearch();
}

const hasDataFrom = computed(() => {
  return (searchResults.value?.from?.length || 0) > 0 ||
    (recentSearches.value?.from?.length || 0) > 0 ||
    (popularDestinations.value?.from?.length || 0) > 0;
})

const hasDataTo = computed(() => {
  return (searchResults.value?.to?.length || 0) > 0 ||
    (recentSearches.value?.to?.length || 0) > 0 ||
    (popularDestinations.value?.to?.length || 0) > 0;
})

onMounted(() => {
  store.initialDefaultDate();
  store.initSearchBox();
  initDatePicker();
  store.loadSearchHistory();
})
</script>
<template>
  <form class="search" @submit.prevent="submitSearch" role="search" :aria-label="__('booker.flights')">
    <div class="search__container">
      <div class="search__fligths">
        <div class="search__item">
          <SmartBox @open="onOpenSearch('from')" :title="__('booker.origin_sub')" :OpenBox="OpenSearch.from"
            :CloseBox="CloseSearch.from" :isHeaderActive="false" :hasContent="hasDataFrom" @isMobile="isMobile = $event">
            <template #trigger>
              <SearchInput id="origin" :label="__('booker.origin')" :placeholder="__('booker.origin')"
                :activeFocus="!isMobile" v-model="form.from" @input="onSearch($event, false, 'from')" :debounce="700"
                :selectOnFocus="!isMobile" :autocomplete="'off'" :spellcheck="false" :error="!!fieldErrors.from"
                :errorMessage="fieldErrors.from" :readonly="isMobile">
                <template #icon>
                  <span class="search__icon font-20 icons-plane-departure"></span>
                </template>
              </SearchInput>
            </template>
            <template #content>
              <DestinationSearch :product="'P'" :recentSearches="recentSearches.from" :origin="'from'"
                :popularDestinations="popularDestinations.from" @update:query="onSearch($event, true, 'from')" :typeBooker="'from'"
                :destination="form.from" :imgUrl="algoliaUrl" :searchResults="searchResults.from"
                @update:selectedDestination="onSelectDestination($event, 'from', '')" :lastQuery="lastQuery"
                :label="__('booker.origin')" :placeholder="''" :icon="'icons-plane-departure'">
              </DestinationSearch>
            </template>
          </SmartBox>
        </div>
        <div class="search__item">
          <SmartBox @open="onOpenSearch('to')" :title="__('booker.destination_sub')" :OpenBox="OpenSearch.to"
            :CloseBox="CloseSearch.to" :isHeaderActive="false" :hasContent="hasDataTo" @isMobile="isMobile = $event">
            <template #trigger>
              <SearchInput id="destination" :label="__('booker.destination')" :placeholder="__('booker.destination')"
                :selectOnFocus="!isMobile" :activeFocus="!isMobile" v-model="form.to" @input="onSearch($event, false, 'to')"
                :debounce="700" :autocomplete="'off'" :spellcheck="false" :error="!!fieldErrors.to"
                :errorMessage="fieldErrors.to" :readonly="isMobile">
                <template #icon>
                  <span class="search__icon font-20 icons-plane-arrival"></span>
                </template>
              </SearchInput>
            </template>
            <template #content>
              <DestinationSearch :product="'P'" :recentSearches="recentSearches.to"
                :popularDestinations="popularDestinations.to" @update:query="onSearch($event, true, 'to')" :typeBooker="'to'"
                :destination="form.to" :imgUrl="algoliaUrl" :searchResults="searchResults.to"
                @update:selectedDestination="onSelectDestination($event, 'to', '_to')" :lastQuery="lastQuery"
                :label="__('booker.destination')" :placeholder="''" :icon="'icons-plane-arrival'">
              </DestinationSearch>
            </template>
          </SmartBox>
        </div>
      </div>
      <div class="search__guest">
        <div class="search__item">
          <SmartBox ref="calendarBox" :title="__('booker.dates')" :isHeaderActive="false" :mobile-breakpoint="768">
            <template #trigger>
              <SearchInput id="calendar-checkIn-package" type="text" required name="calendar-checkIn-package"
                :activeFocus="true" :label="__('booker.dates')" :readonly="true" v-model="form.dates"
                :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
                <template #icon>
                  <span class="icon font-icons icons-calendar-depart font-20"></span>
                </template>
              </SearchInput>
            </template>
            <template #content>
              <BookerCalendar :modelValue="calendarDates" :currency="config.currency" mode="range" :unit="'nights'"
                :minDays="2" :nOffset="-1" :minDate="defaultDate" @select="onSelectDates" @close="calendarBox.close()" />
            </template>
          </SmartBox>
        </div>
        <div class="search__item">
          <SmartBox ref="guestsBox" :title="__('booker.guests_sub')" :isHeaderActive="false" @close="onCloseGuests"
            :mobile-breakpoint="768">
            <template #trigger>
              <SearchInput id="guests" :label="__('booker.guests')" :placeholder="form.guests" :readonly="true"
                :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests"
                :errorMessage="fieldErrors.guests">
                <template #icon>
                  <span class="icon font-icons icons-guests font-20"></span>
                </template>
              </SearchInput>
            </template>
            <template #content>
              <Occupancy :product="'P'" :settingPaxes="settingPaxes" :counts="book.paxes" @change="onChangePaxes"
                @apply="onApplyPaxes" @cancel="onCancelPaxes">
              </Occupancy>
            </template>
          </SmartBox>
        </div>
        <div class="search__action">
          <button type="submit" class="search__button btnPrimary btnPrimary--md">
            {{ __('booker.button') }}
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped lang="scss">
.search {
  background-color: var(--bg-info-strong);
  padding: var(--space-16);
  border-radius: var(--space-12);
  box-sizing: border-box;
  background-color: var(--bg-base);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  box-shadow: var(--shadow-200);
  
  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: auto;
    padding: var(--space-20) 0px;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    box-shadow: none;
    margin: 0px auto;
    border: 0px;
  }

  @media (min-width: 1280px) {
    width: 100%;
    padding: var(--space-20);
    border-radius: var(--space-12);
    border: 1px solid var(--border-subtle);
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      gap: var(--space-20);
    }

    @media (min-width: 1280px) {
      flex-direction: row;
      align-items: center;
      gap: var(--space-12);
    }
  }

  &__fligths,
  &__guest {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
      gap: var(--space-32);
    }

    @media (min-width: 1280px) {
      flex: 1;
      gap: var(--space-12);
    }
  }

  &__fligths {
    @media (min-width: 1280px) {
      flex: 1;
      max-width: 552px;
    }
  }

  &__item {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  &__action {
    width: 100%;

    @media (min-width: 768px) {
      width: 160px;
    }

    @media (min-width: 1280px) {
      width: 114px;
    }
  }

  &__button {
    width: 100%;
  }
}
</style>
